import { ReactNode, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Sidebar } from "./Sidebar";
import { TopBar } from "./TopBar";
import { CommandPalette } from "./CommandPalette";
import { fetchCurrentUser } from "@/api/auth";
import { fetchKpis } from "@/api/dashboard";
import { getMailConfig } from "@/api/settings";
import { applyTweaks, loadTweaks, saveTweaks, Theme } from "@/lib/theme";

type Props = { children: ReactNode };

export function AppShell({ children }: Props) {
  const [collapsed, setCollapsed] = useState(false);
  const [tweaks, setTweaks] = useState(() => loadTweaks());
  const [cmdOpen, setCmdOpen] = useState(false);

  useEffect(() => {
    applyTweaks(tweaks);
    saveTweaks(tweaks);
  }, [tweaks]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCmdOpen(true);
      }
      if (e.key === "Escape") setCmdOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const userQuery = useQuery({ queryKey: ["me"], queryFn: fetchCurrentUser, retry: false });
  const kpisQuery = useQuery({ queryKey: ["dashboard", "kpis"], queryFn: fetchKpis, retry: false, enabled: !!userQuery.data });
  const anfragenBadge = kpisQuery.data?.open_inquiries ?? 0;
  const mailQuery = useQuery({ queryKey: ["me-mail-settings"], queryFn: getMailConfig, retry: false, enabled: !!userQuery.data });
  const posteingangAktiv = mailQuery.data?.posteingang_aktiv === "1";

  function toggleTheme() {
    setTweaks((t) => ({ ...t, theme: (t.theme === "dark" ? "light" : "dark") as Theme }));
  }

  return (
    <div
      style={{
        display: "flex",
        minHeight: "100vh",
        background: "var(--bg)",
        fontFamily: "var(--font-body)",
        color: "var(--fg)",
      }}
    >
      <Sidebar
        collapsed={collapsed}
        onToggle={() => setCollapsed((c) => !c)}
        user={userQuery.data ?? null}
        anfragenBadge={anfragenBadge}
        posteingangAktiv={posteingangAktiv}
      />
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <TopBar theme={tweaks.theme} onToggleTheme={toggleTheme} onOpenCmd={() => setCmdOpen(true)} />
        <main
          style={{
            flex: 1,
            padding: "28px 32px",
            maxWidth: 1440,
            width: "100%",
            margin: "0 auto",
          }}
        >
          {children}
        </main>
      </div>
      <CommandPalette open={cmdOpen} onClose={() => setCmdOpen(false)} posteingangAktiv={posteingangAktiv} />
    </div>
  );
}
